import { useEffect, useState } from 'react';

interface VectorSearchConfig {
  vectorSearchK: number;
  vectorBoost: number;
  textBoost: number;
  minScoreVectorKnn: number;
  minScoreVectorHybrid: number;
  updatedAt?: string;
}

interface VectorSearchConfigProps {
  onSave: (config: VectorSearchConfig) => void;
  onReset: () => void;
  saving: boolean;
}

const DEFAULT_CONFIG: VectorSearchConfig = {
  vectorSearchK: 100,
  vectorBoost: 2.0,
  textBoost: 1.0,
  minScoreVectorKnn: 0.3,
  minScoreVectorHybrid: 0.3,
};

export function VectorSearchConfig({ onSave, onReset, saving }: VectorSearchConfigProps) {
  const [config, setConfig] = useState<VectorSearchConfig>(DEFAULT_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/vector-search-config`);
      if (!response.ok) {
        // If config doesn't exist, use defaults
        if (response.status === 404) {
          setConfig(DEFAULT_CONFIG);
          setHasChanges(false);
          return;
        }
        throw new Error('Failed to fetch vector search config');
      }
      const data = await response.json();
      setConfig(data);
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching vector search config:', err);
      setConfig(DEFAULT_CONFIG);
      setHasChanges(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof VectorSearchConfig, value: number) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(config);
    setHasChanges(false);
  };

  const handleReset = () => {
    onReset();
    fetchConfig();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-zinc-600">Loading vector search configuration...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 py-6">
      <div className="mb-8">
        <h2 className="text-3xl font-semibold text-zinc-900 mb-2">Vector Search Configuration</h2>
        <p className="text-base text-zinc-600 leading-relaxed">
          Configure vector search parameters for semantic similarity and hybrid search functionality.
        </p>
      </div>

      <div className="mb-8">
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-5">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">How Vector Search Works</h3>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div>
              <h4 className="text-sm font-semibold text-zinc-900 mb-2">🧠 Semantic Search</h4>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Vector search uses AI embeddings to find documents with similar meaning, not just matching keywords.
                This enables more intelligent search results.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-zinc-900 mb-2">⚖️ Hybrid Approach</h4>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Combines vector similarity (semantic understanding) with traditional text matching for optimal
                relevance.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-zinc-900 mb-2">📊 Scoring</h4>
              <p className="text-sm text-zinc-700 leading-relaxed">
                <strong>Final Score = (Vector Score × Vector Boost) + (Text Score × Text Boost)</strong>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8 mb-8">
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-5 pb-2 border-b-2 border-blue-500">
            Search Parameters
          </h3>

          <div className="mb-6">
            <label htmlFor="vectorSearchK" className="block mb-2">
              <span className="block font-semibold text-zinc-900 mb-1">Results Limit (K)</span>
              <span className="block text-sm text-zinc-600">
                Maximum number of results to return from vector search
              </span>
            </label>
            <div className="flex items-center gap-2 mb-1">
              <input
                id="vectorSearchK"
                type="number"
                min="10"
                max="1000"
                step="10"
                value={config.vectorSearchK}
                onChange={(e) => handleChange('vectorSearchK', parseInt(e.target.value, 10) || 100)}
                className="px-3 py-2 border border-zinc-300 rounded text-base w-32 text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-sm text-zinc-600 min-w-[60px]">results</span>
            </div>
            <div className="text-xs text-zinc-500 italic">
              Recommended: 100-500. Higher values may impact performance.
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-5 pb-2 border-b-2 border-blue-500">Boost Values</h3>

          <div className="mb-6">
            <label htmlFor="vectorBoost" className="block mb-2">
              <span className="block font-semibold text-zinc-900 mb-1">Vector Boost</span>
              <span className="block text-sm text-zinc-600">Weight multiplier for semantic similarity scores</span>
            </label>
            <div className="flex items-center gap-2 mb-1">
              <input
                id="vectorBoost"
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={config.vectorBoost}
                onChange={(e) => handleChange('vectorBoost', parseFloat(e.target.value) || 2.0)}
                className="px-3 py-2 border border-zinc-300 rounded text-base w-32 text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-sm text-zinc-600 min-w-[60px]">×</span>
            </div>
            <div className="text-xs text-zinc-500 italic">
              Higher values prioritize semantic similarity. Default: 2.0
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="textBoost" className="block mb-2">
              <span className="block font-semibold text-zinc-900 mb-1">Text Boost</span>
              <span className="block text-sm text-zinc-600">
                Weight multiplier for traditional text matching scores
              </span>
            </label>
            <div className="flex items-center gap-2 mb-1">
              <input
                id="textBoost"
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={config.textBoost}
                onChange={(e) => handleChange('textBoost', parseFloat(e.target.value) || 1.0)}
                className="px-3 py-2 border border-zinc-300 rounded text-base w-32 text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-sm text-zinc-600 min-w-[60px]">×</span>
            </div>
            <div className="text-xs text-zinc-500 italic">
              Higher values prioritize exact keyword matches. Default: 1.0
            </div>
          </div>

          <div className="mt-5 p-4 bg-zinc-50 rounded-md">
            <h4 className="mb-4 text-base font-semibold text-zinc-900">Current Balance</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-[120px_1fr_60px] items-center gap-3">
                <span className="text-sm font-medium text-zinc-700">Vector Priority</span>
                <div className="h-5 bg-zinc-200 rounded-full overflow-hidden relative">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                    style={{ width: `${(config.vectorBoost / (config.vectorBoost + config.textBoost)) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-zinc-900 text-right">
                  {Math.round((config.vectorBoost / (config.vectorBoost + config.textBoost)) * 100)}%
                </span>
              </div>
              <div className="grid grid-cols-[120px_1fr_60px] items-center gap-3">
                <span className="text-sm font-medium text-zinc-700">Text Priority</span>
                <div className="h-5 bg-zinc-200 rounded-full overflow-hidden relative">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-300"
                    style={{ width: `${(config.textBoost / (config.vectorBoost + config.textBoost)) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-zinc-900 text-right">
                  {Math.round((config.textBoost / (config.vectorBoost + config.textBoost)) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-5 pb-2 border-b-2 border-blue-500">
            Quality Thresholds
          </h3>

          <div className="mb-6">
            <label htmlFor="minScoreVectorKnn" className="block mb-2">
              <span className="block font-semibold text-zinc-900 mb-1">Vector k-NN Minimum Score</span>
              <span className="block text-sm text-zinc-600">
                Minimum similarity score for pure vector search results
              </span>
            </label>
            <div className="flex items-center gap-2 mb-1">
              <input
                id="minScoreVectorKnn"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={config.minScoreVectorKnn}
                onChange={(e) => handleChange('minScoreVectorKnn', parseFloat(e.target.value) || 0.3)}
                className="px-3 py-2 border border-zinc-300 rounded text-base w-32 text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-sm text-zinc-600 min-w-[60px]">score</span>
            </div>
            <div className="text-xs text-zinc-500 italic">
              Range: 0.0-1.0. Higher values filter out less relevant results.
            </div>
          </div>

          <div className="mb-0">
            <label htmlFor="minScoreVectorHybrid" className="block mb-2">
              <span className="block font-semibold text-zinc-900 mb-1">Hybrid Search Minimum Score</span>
              <span className="block text-sm text-zinc-600">
                Minimum combined score for hybrid vector + text search
              </span>
            </label>
            <div className="flex items-center gap-2 mb-1">
              <input
                id="minScoreVectorHybrid"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={config.minScoreVectorHybrid}
                onChange={(e) => handleChange('minScoreVectorHybrid', parseFloat(e.target.value) || 0.3)}
                className="px-3 py-2 border border-zinc-300 rounded text-base w-32 text-right focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
              <span className="text-sm text-zinc-600 min-w-[60px]">score</span>
            </div>
            <div className="text-xs text-zinc-500 italic">
              Range: 0.0-1.0. Controls quality threshold for hybrid results.
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-zinc-900 mb-4">📋 Configuration Guidelines</h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h4 className="text-base font-semibold text-zinc-700 mb-3">Recommended Settings</h4>
              <ul className="space-y-2">
                <li className="text-sm text-zinc-700 leading-relaxed">
                  <strong className="text-zinc-900">Balanced Search</strong>: Vector 2.0, Text 1.0
                </li>
                <li className="text-sm text-zinc-700 leading-relaxed">
                  <strong className="text-zinc-900">Semantic Focus</strong>: Vector 3.0, Text 0.5
                </li>
                <li className="text-sm text-zinc-700 leading-relaxed">
                  <strong className="text-zinc-900">Keyword Focus</strong>: Vector 1.0, Text 2.0
                </li>
                <li className="text-sm text-zinc-700 leading-relaxed">
                  <strong className="text-zinc-900">Quality Threshold</strong>: 0.2-0.4 for most use cases
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold text-zinc-700 mb-3">Performance Tips</h4>
              <ul className="space-y-2">
                <li className="text-sm text-zinc-700 leading-relaxed">Lower K values improve response time</li>
                <li className="text-sm text-zinc-700 leading-relaxed">
                  Higher thresholds reduce result count but improve quality
                </li>
                <li className="text-sm text-zinc-700 leading-relaxed">Test changes with representative queries</li>
                <li className="text-sm text-zinc-700 leading-relaxed">Monitor search performance metrics</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-4 p-5 bg-zinc-50 rounded-lg mb-5">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-green-600 text-white border-none px-6 py-3 rounded-md text-base font-medium cursor-pointer transition-colors hover:bg-green-700 disabled:bg-zinc-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="bg-red-500 text-white border-none px-6 py-3 rounded-md text-base font-medium cursor-pointer transition-colors hover:bg-red-600 disabled:bg-zinc-400 disabled:cursor-not-allowed"
        >
          Reset to Defaults
        </button>
      </div>

      {config.updatedAt && (
        <div className="text-center text-zinc-500 text-sm italic">
          Last updated: {new Date(config.updatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}
